import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import ProfileAvatarImage from '@/components/ProfileAvatarImage';
import { Badge } from '@/components/ui/badge';
import { User, Settings, LogOut, Shield, Sun, Moon, Monitor, Menu, X, Bell, CheckCheck, MessageCircle, Heart, Flag, UserPlus, Bookmark, EyeOff, Trash2, VolumeX, Volume2, UserCheck, AlertTriangle, Users, ShieldAlert, Crown, Info } from 'lucide-react';
import { ShhIcon } from '@/components/icons/ShhIcon';
import axios from 'axios';
import { API_URL } from '@/lib/api';
const DEFAULT_AVATAR = '/shh-logo-new.jpeg';

// Global event bus for triggering verify sparkle from anywhere
const verifySparkleListeners = new Set();
export const triggerVerifySparkle = () => verifySparkleListeners.forEach(fn => fn());
const useVerifySparkle = () => {
  const [sparkling, setSparkling] = React.useState(false);
  React.useEffect(() => {
    const handler = () => { setSparkling(true); setTimeout(() => setSparkling(false), 1500); };
    verifySparkleListeners.add(handler);
    return () => verifySparkleListeners.delete(handler);
  }, []);
  return sparkling;
};

const getAvatarUrl = (pic) => {
  if (!pic) return DEFAULT_AVATAR;
  if (pic.startsWith('/api/')) return `${API_URL}${pic}`;
  return pic;
};

// Compact circular progress ring for header
const ProfileCompletionRing = ({ user, onClick }) => {
  if (!user) return null;
  const fields = [
    !!user.display_name,
    !!user.bio,
    !!user.profile_headline,
    !!user.profile_picture,
    !!user.profile_image_url_private,
  ];
  const filled = fields.filter(Boolean).length;
  const pct = Math.round((filled / 5) * 100);
  if (pct >= 100) return null;

  const size = 28;
  const stroke = 3;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashoffset = circumference - (pct / 100) * circumference;

  return (
    <button
      onClick={onClick}
      className="relative flex items-center justify-center rounded-full hover:bg-muted/50 transition-colors p-1"
      title={`Profile ${pct}% complete`}
      data-testid="header-profile-completion"
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="currentColor" strokeWidth={stroke} fill="none" className="text-muted/40" />
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="currentColor" strokeWidth={stroke} fill="none" className="text-amber-500" strokeDasharray={circumference} strokeDashoffset={dashoffset} strokeLinecap="round" />
      </svg>
      <span className="absolute text-[8px] font-bold text-amber-600 dark:text-amber-400">{pct}%</span>
    </button>
  );
};

// Verify Account badge — shows when user is logged in but email not verified
const VerifyBadge = ({ user, onClick }) => {
  const sparkling = useVerifySparkle();
  if (!user || user.email_verified || user.oauth_provider) return null;

  return (
    <button
      onClick={onClick}
      className={`relative flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-300 ${
        sparkling
          ? 'bg-amber-400 text-amber-950 shadow-[0_0_16px_4px_rgba(251,191,36,0.5)] scale-110'
          : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800/50'
      }`}
      title="Verify your account to participate"
      data-testid="verify-account-badge"
    >
      <ShieldAlert className={`w-3.5 h-3.5 ${sparkling ? 'animate-bounce' : ''}`} />
      <span>Verify Account</span>
      {sparkling && (
        <>
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-300 rounded-full animate-ping" />
          <span className="absolute -bottom-0.5 -left-0.5 w-1.5 h-1.5 bg-amber-400 rounded-full animate-ping" style={{ animationDelay: '0.2s' }} />
          <span className="absolute -top-0.5 left-1/2 w-1.5 h-1.5 bg-yellow-300 rounded-full animate-ping" style={{ animationDelay: '0.4s' }} />
        </>
      )}
    </button>
  );
};

const NotifIcon = ({ type }) => {
  switch (type) {
    case 'reply': return <MessageCircle className="w-4 h-4 text-blue-500" />;
    case 'reaction': return <Heart className="w-4 h-4 text-pink-500" />;
    case 'post_follow': return <Bookmark className="w-4 h-4 text-green-500" />;
    case 'admin_report': return <Flag className="w-4 h-4 text-red-500" />;
    case 'admin_comment_approval': return <MessageCircle className="w-4 h-4 text-blue-500" />;
    case 'admin_unverified': return <UserPlus className="w-4 h-4 text-amber-500" />;
    case 'comment_hidden': return <EyeOff className="w-4 h-4 text-orange-500" />;
    case 'comment_removed': return <Trash2 className="w-4 h-4 text-red-500" />;
    case 'comment_declined': return <Trash2 className="w-4 h-4 text-orange-600" />;
    case 'user_muted': return <VolumeX className="w-4 h-4 text-orange-600" />;
    case 'user_unmuted': return <Volume2 className="w-4 h-4 text-green-600" />;
    case 'user_unbanned': return <UserCheck className="w-4 h-4 text-green-600" />;
    case 'admin_tampering': return <AlertTriangle className="w-4 h-4 text-red-600" />;
    case 'confidant_request': return <Users className="w-4 h-4 text-amber-500" />;
    case 'confidant_accepted': return <Users className="w-4 h-4 text-green-500" />;
    case 'display_name_flagged': return <ShieldAlert className="w-4 h-4 text-amber-600" />;
    default: return <Bell className="w-4 h-4" />;
  }
};

const getNotifText = (n) => {
  const d = n.data || {};
  const count = n.count || 1;
  const others = count > 1 ? ` and ${count - 1} other${count > 2 ? 's' : ''}` : '';
  switch (n.type) {
    case 'reply':
      return `${d.actor_name}${others} replied to your comment`;
    case 'reaction':
      return `${d.actor_name}${others} reacted ${d.emoji || ''} to your comment`;
    case 'post_follow':
      return `New comment on a confession you're following (${d.post_tag || ''})`;
    case 'admin_report':
      return `New report: "${d.comment_snippet || '...'}"`;
    case 'admin_comment_approval':
      return `Comment awaiting approval: "${d.comment_snippet || '...'}"`;
    case 'admin_unverified':
      const uCount = d.unverified_count || 1;
      return uCount === 1
        ? `There is 1 unverified account awaiting review`
        : `There are ${uCount} unverified accounts awaiting review`;
    case 'comment_hidden':
      return `Your comment was hidden by a moderator: "${d.snippet || '...'}"`;
    case 'comment_removed':
      return `Your comment was removed by a moderator: "${d.snippet || '...'}"`;
    case 'comment_declined':
      return `Your comment was declined by a moderator. Reason: ${d.reason || 'No reason provided.'}`;
    case 'user_muted':
      return `You have been muted for ${d.days || '?'} day(s). Reason: ${d.reason || 'No reason provided'}`;
    case 'user_unmuted':
      return `Your mute has been lifted. You can participate again!`;
    case 'user_unbanned':
      return `Your ban has been lifted. Welcome back!`;
    case 'admin_tampering':
      return `Tampering detected from user "${d.display_name || 'Unknown'}": ${d.event_type || 'unknown'}`;
    case 'confidant_request':
      return `${d.sender_name || 'Someone'} wants to be your confidant${d.message ? ': "' + d.message.slice(0, 50) + '"' : ''}`;
    case 'confidant_accepted':
      return `${d.accepter_name || 'Someone'} accepted your confidant request!`;
    case 'display_name_flagged':
      return 'Your username was flagged by moderation. You need to choose a new one before continuing.';
    default:
      return 'New notification';
  }
};

const getNotifLink = (n) => {
  const d = n.data || {};
  if (n.type === 'confidant_request') return '/confidants';
  if (n.type === 'confidant_accepted') return `/profile/${d.accepter_id || ''}`;
  if (n.type === 'admin_report') return '/admin/reports';
  if (n.type === 'admin_comment_approval') return '/admin/approvals';
  if (n.type === 'admin_unverified') return '/admin/users';
  if (n.type === 'display_name_flagged') return `/profile/${d.target_profile_user_id || ''}?setup=1`;
  if (n.type === 'user_muted' || n.type === 'user_unmuted' || n.type === 'user_unbanned') return '/';
  if (n.type === 'admin_tampering') return `/profile/${d.user_id || ''}`;
  if (d.post_id) return `/post/${d.post_id}`;
  return '/';
};

const infoLinks = [
  { to: '/about', label: 'About' },
  { to: '/rules', label: 'Rules' },
  { to: '/?tab=contributors', label: 'Contributors' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/faq', label: 'FAQ' },
  { to: '/moderation-policy', label: 'Moderation' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
  { to: '/contact', label: 'Contact' },
];

const InfoDropdown = ({ onNavigate }) => {
  const navigate = useNavigate();
  const go = (to) => {
    navigate(to);
    if (onNavigate) onNavigate();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="rounded-full px-3" data-testid="info-menu-trigger">
          <Info className="w-4 h-4" />
          <span className="hidden lg:inline">Info</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {infoLinks.map((link) => (
          <DropdownMenuItem key={link.to} onClick={() => go(link.to)}>
            {link.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Layout = () => {
  const { user, logout, isAdmin, adminTabs } = useAuth();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/api/notifications?limit=20`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) {
      if (e?.response?.status === 401) {
        setNotifications([]);
        setUnreadCount(0);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {}
  };

  const handleNotifClick = async (notif) => {
    if (!notif.is_read) {
      try {
        await axios.post(`${API_URL}/api/notifications/${notif.notification_id}/read`);
        setNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (e) {}
    }
    setNotifOpen(false);
    navigate(getNotifLink(notif));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const showAdminLink = isAdmin || (adminTabs && adminTabs.length > 0);
  const ThemeIcon = resolvedTheme === 'dark' ? Moon : Sun;
  const displayName = user?.display_name || user?.username;
  const showVerifyWarning = !!user && !user.email_verified && !user.oauth_provider;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-10 h-10 rounded-lg overflow-hidden group-hover:scale-105 transition-transform">
              <ShhIcon className="w-full h-full" />
            </div>
            <div className="hidden sm:block">
              <span className="font-serif font-bold text-lg text-foreground leading-tight">Secrets Club</span>
              <p className="text-xs text-muted-foreground -mt-0.5">secretsclub.ca — Anonymous Confessions</p>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-2">
            {user && (
              <>
                <ProfileCompletionRing user={user} onClick={() => navigate(`/profile/${user.user_id}`)} />
                <VerifyBadge user={user} onClick={() => navigate('/verify-account')} />
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="theme-toggle">
                  <ThemeIcon className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setTheme('light')} data-testid="theme-light">
                  <Sun className="w-4 h-4 mr-2" /> Light
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')} data-testid="theme-dark">
                  <Moon className="w-4 h-4 mr-2" /> Dark
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('auto')} data-testid="theme-auto">
                  <Monitor className="w-4 h-4 mr-2" /> System
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <>
                {/* Notification Bell */}
                <DropdownMenu open={notifOpen} onOpenChange={(open) => { setNotifOpen(open); if (open) fetchNotifications(); }}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full relative" data-testid="notification-bell">
                      <Bell className="w-5 h-5" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notification-count">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                    <div className="flex items-center justify-between px-3 py-2 border-b">
                      <span className="font-semibold text-sm">Notifications</span>
                      {unreadCount > 0 && (
                        <button 
                          onClick={handleMarkAllRead} 
                          className="text-xs text-forest-600 hover:text-forest-700 flex items-center gap-1"
                          data-testid="mark-all-read-btn"
                        >
                          <CheckCheck className="w-3 h-3" /> Mark all read
                        </button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <div className="py-8 text-center text-sm text-muted-foreground">
                        No notifications yet
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <DropdownMenuItem
                          key={notif.notification_id}
                          onClick={() => handleNotifClick(notif)}
                          className={`flex items-start gap-3 px-3 py-3 cursor-pointer ${!notif.is_read ? 'bg-forest-50/50 dark:bg-forest-900/20' : ''}`}
                          data-testid={`notification-${notif.notification_id}`}
                        >
                          <div className="mt-0.5 flex-shrink-0">
                            <NotifIcon type={notif.type} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm leading-snug ${!notif.is_read ? 'font-medium' : 'text-muted-foreground'}`}>
                              {getNotifText(notif)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {new Date(notif.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          {!notif.is_read && (
                            <div className="w-2 h-2 rounded-full bg-forest-500 flex-shrink-0 mt-1.5" />
                          )}
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <InfoDropdown />

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 rounded-full px-2" data-testid="user-menu-trigger">
                      <Avatar className="w-8 h-8">
                        <ProfileAvatarImage
                          src={getAvatarUrl(user.profile_picture)}
                          alt={displayName}
                          zoom={user.profile_picture_zoom}
                          positionX={user.profile_picture_position_x}
                          positionY={user.profile_picture_position_y}
                        />
                        <AvatarFallback>
                          <img src={DEFAULT_AVATAR} alt="" className="w-full h-full object-cover" />
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden lg:inline font-medium">{displayName}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate(`/profile/${user.user_id}`)} data-testid="profile-link">
                      <User className="w-4 h-4 mr-2" /> Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/confidants')} data-testid="confidants-link">
                      <Users className="w-4 h-4 mr-2" /> Confidants
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/membership')} data-testid="membership-link">
                      <Crown className="w-4 h-4 mr-2" /> Membership
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/?tab=contributors')} data-testid="contributors-link">
                      <Heart className="w-4 h-4 mr-2" /> Contributors
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-link">
                      <Settings className="w-4 h-4 mr-2" /> Settings
                    </DropdownMenuItem>
                    {showAdminLink && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-link">
                          <Shield className="w-4 h-4 mr-2" /> Admin Panel
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <InfoDropdown />
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="login-btn">
                  Sign In
                </Button>
                <Button className="btn-primary" onClick={() => navigate('/register')} data-testid="register-btn">
                  Join Now
                </Button>
              </div>
            )}
          </nav>

          <div className="flex md:hidden items-center gap-2">
            {user && (
              <Button variant="ghost" size="icon" className="relative" onClick={() => { const next = !notifOpen; setNotifOpen(next); if (next) fetchNotifications(); }} data-testid="mobile-notification-bell">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Button>
            )}
            <InfoDropdown />
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {showVerifyWarning && (
          <button
            type="button"
            onClick={() => navigate('/verify-account')}
            className="w-full border-t border-amber-300/50 bg-amber-100/90 dark:bg-amber-900/35 hover:bg-amber-200/90 dark:hover:bg-amber-900/50 transition-colors"
            data-testid="verify-account-warning-bar"
          >
            <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-center gap-2 text-sm text-amber-800 dark:text-amber-200">
              <ShieldAlert className="w-4 h-4 flex-shrink-0" />
              <span>Your account is not verified yet. Verify it to comment, react, report, and submit confessions.</span>
            </div>
          </button>
        )}

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-lg animate-fade-in">
            <div className="px-4 py-4 space-y-3">
              {user && (
                <div className="flex items-center gap-2 pb-2">
                  <ProfileCompletionRing user={user} onClick={() => { navigate(`/profile/${user.user_id}`); setMobileMenuOpen(false); }} />
                  <VerifyBadge user={user} onClick={() => { navigate('/verify-account'); setMobileMenuOpen(false); }} />
                  {(() => {
                    const fields = [!!user.display_name, !!user.bio, !!user.profile_headline, !!user.profile_picture, !!user.profile_image_url_private];
                    const pct = Math.round((fields.filter(Boolean).length / 5) * 100);
                    return pct < 100 ? <span className="text-xs text-amber-600 dark:text-amber-400">Profile {pct}% complete</span> : null;
                  })()}
                </div>
              )}
              <div className="flex gap-2 pb-3 border-b border-border">
                <Button variant={theme === 'light' ? 'default' : 'outline'} size="sm" onClick={() => setTheme('light')} className="flex-1">
                  <Sun className="w-4 h-4 mr-1" /> Light
                </Button>
                <Button variant={theme === 'dark' ? 'default' : 'outline'} size="sm" onClick={() => setTheme('dark')} className="flex-1">
                  <Moon className="w-4 h-4 mr-1" /> Dark
                </Button>
                <Button variant={theme === 'auto' ? 'default' : 'outline'} size="sm" onClick={() => setTheme('auto')} className="flex-1">
                  <Monitor className="w-4 h-4 mr-1" /> Auto
                </Button>
              </div>
              {user ? (
                <>
                  <Link to={`/profile/${user.user_id}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
                    <Avatar className="w-10 h-10">
                      <ProfileAvatarImage
                        src={getAvatarUrl(user.profile_picture)}
                        alt={displayName}
                        zoom={user.profile_picture_zoom}
                        positionX={user.profile_picture_position_x}
                        positionY={user.profile_picture_position_y}
                      />
                      <AvatarFallback><img src={DEFAULT_AVATAR} alt="" className="w-full h-full object-cover" /></AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{displayName}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </Link>
                  <Link to="/settings" className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
                    <Settings className="w-5 h-5" /> Settings
                  </Link>
                  <Link to="/confidants" className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
                    <Users className="w-5 h-5" /> Confidants
                  </Link>
                  <Link to="/membership" className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted" onClick={() => setMobileMenuOpen(false)}>
                    <Crown className="w-5 h-5" /> Membership
                  </Link>
                  <Link to="/?tab=contributors" className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-contributors-link">
                    <Heart className="w-5 h-5" /> Contributors
                  </Link>
                  {showAdminLink && (
                    <Link to="/admin" className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted text-forest-700 dark:text-forest-400" onClick={() => setMobileMenuOpen(false)}>
                      <Shield className="w-5 h-5" /> Admin Panel
                    </Link>
                  )}
                  <Button variant="destructive" className="w-full" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </Button>
                </>
              ) : (
                <div className="space-y-2">
                  <Button variant="outline" className="w-full" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>Sign In</Button>
                  <Button className="w-full btn-primary" onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}>Join Now</Button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-border mt-auto">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
              <div className="w-6 h-6 rounded overflow-hidden">
                <ShhIcon className="w-full h-full" />
              </div>
              <span className="font-serif font-medium">Secrets Club</span>
            </div>
            <nav
              aria-label="Footer"
              className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-muted-foreground"
              style={{ textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
            >
              <Link to="/about" className="hover:text-foreground hover:underline">About</Link>
              <Link to="/rules" className="hover:text-foreground hover:underline">Rules</Link>
              <Link to="/how-it-works" className="hover:text-foreground hover:underline">How It Works</Link>
              <Link to="/faq" className="hover:text-foreground hover:underline">FAQ</Link>
              <Link to="/moderation-policy" className="hover:text-foreground hover:underline">Moderation</Link>
              <Link to="/privacy" className="hover:text-foreground hover:underline">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground hover:underline">Terms</Link>
              <Link to="/contact" className="hover:text-foreground hover:underline">Contact</Link>
            </nav>
            <div className="flex items-center gap-4" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
              <p className="text-sm text-muted-foreground">
                &copy; {new Date().getFullYear()} Secrets Club. Truly Anonymous Confessions & Advice.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
